import { useEffect, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  useAdminIntakeCommands,
  useAdminIntakeObligations,
  useAdminIntakeSourceTask,
  type AdminIntakeQuery,
} from "@/api/center-intake-obligations";
import { useOperators } from "@/api/operators";
import { useTasks } from "@/api/tasks";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { useSession } from "@/services/auth0/session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ViewTripButton } from "@/features/trips/view-trip-button";
import { TaskTimeline } from "@/features/shipments/shipments-table";
import { getOperatorDisplayName } from "@/lib/safe-access";
import { CenterIntakeReceiving } from "./center-intake-receiving";

export function AdminIntakeSection() {
  const { user, token, hasRole } = useSession();
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scopeKey = JSON.stringify([user?.id, carrierIds, dispatchCenterIds]);
  if (!token || !user || !hasRole("ADMIN")) return null;
  return (
    <ScopedIntake
      key={scopeKey}
      scopeKey={scopeKey}
      carrierIds={carrierIds.join(",")}
      dispatchCenterIds={dispatchCenterIds.join(",")}
    />
  );
}
function ScopedIntake({
  scopeKey,
  carrierIds,
  dispatchCenterIds,
}: {
  scopeKey: string;
  carrierIds: string;
  dispatchCenterIds: string;
}) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [operatorSearch, setOperatorSearch] = useState("");
  const [filters, setFilters] = useState<
    Pick<AdminIntakeQuery, "view" | "operatorId" | "sourceType">
  >({ view: "outstanding", operatorId: "", sourceType: "" });
  const [context, setContext] = useState<{
    shipmentId: string;
    taskId: string;
    taskNumber: number;
  } | null>(null);
  const query = useAdminIntakeObligations({
    page,
    search,
    ...filters,
    carrierIds,
    dispatchCenterIds: dispatchCenterIds || undefined,
  });
  const { receive, report } = useAdminIntakeCommands();
  const client = useQueryClient();
  const operators = useOperators({
    limit: 200,
    search: operatorSearch,
    carrierIds,
    dispatchCenterIds: dispatchCenterIds || undefined,
  });
  const denied =
    query.isError &&
    axios.isAxiosError(query.error) &&
    [401, 403, 404].includes(query.error.response?.status ?? 0);
  useEffect(() => {
    if (denied)
      client.removeQueries({
        queryKey: ["admin", "center-intake-obligations"],
      });
  }, [denied, client]);
  const phase = denied
    ? "denied"
    : query.isError
      ? "error"
      : query.isFetching
        ? "refreshing"
        : query.isSuccess
          ? "ready"
          : "loading";
  const rows = phase === "ready" ? (query.data?.data ?? []) : [];
  const operatorOptions = (operators.data?.data ?? []).map((operator) => ({
    id: operator.id,
    name: getOperatorDisplayName(operator),
  }));
  if (
    filters.operatorId &&
    !operatorOptions.some((operator) => operator.id === filters.operatorId)
  ) {
    operatorOptions.push({
      id: filters.operatorId,
      name:
        rows.find((row) => row.custodianOperatorId === filters.operatorId)
          ?.lastConfirmedCustodian ?? filters.operatorId,
    });
  }
  const busy = receive.isPending || report.isPending;
  const centerIds = dispatchCenterIds.split(",").filter(Boolean);
  return (
    <>
      <div className="px-3 pt-3">
        <Input
          aria-label="Search returning Shipment"
          placeholder="Shipment number"
          maxLength={100}
          value={search}
          disabled={busy}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>
      <div className="px-3 pt-2">
        <Input
          aria-label="Search operators"
          placeholder="Search operator names"
          value={operatorSearch}
          disabled={busy}
          onChange={(e) => setOperatorSearch(e.target.value)}
        />
      </div>
      <CenterIntakeReceiving
        scopeKey={`${scopeKey}:${page}:${search}:${JSON.stringify(filters)}`}
        centerId={centerIds.length === 1 ? centerIds[0] : null}
        rows={rows}
        total={query.data?.total ?? 0}
        phase={phase}
        {...filters}
        operatorOptions={operatorOptions}
        onFilterChange={(value) => {
          setFilters(value);
          setPage(1);
          setContext(null);
        }}
        onRefresh={async () => {
          const result = await query.refetch();
          return result.isSuccess;
        }}
        onReceive={(input) => receive.mutateAsync(input)}
        onReport={async (input) => {
          await report.mutateAsync(input);
        }}
        onInspectTask={(id) => {
          const row = rows.find((item) => item.sourceTaskId === id);
          if (row)
            setContext({
              shipmentId: row.shipmentId,
              taskId: row.sourceTaskId,
              taskNumber: row.sourceTaskNumber,
            });
        }}
      />
      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <Button
          size="sm"
          variant="outline"
          disabled={page === 1 || busy || phase !== "ready"}
          onClick={() => setPage((value) => value - 1)}
        >
          Previous goods
        </Button>
        <span className="text-xs">Page {page}</span>
        <Button
          size="sm"
          variant="outline"
          disabled={
            busy || phase !== "ready" || page * 20 >= (query.data?.total ?? 0)
          }
          onClick={() => setPage((value) => value + 1)}
        >
          Next goods
        </Button>
      </div>
      {context && phase === "ready" && (
        <IntakeTaskContext
          {...context}
          carrierIds={carrierIds}
          dispatchCenterIds={dispatchCenterIds}
          onClose={() => setContext(null)}
        />
      )}
    </>
  );
}
function IntakeTaskContext({
  shipmentId,
  taskId,
  taskNumber,
  carrierIds,
  dispatchCenterIds,
  onClose,
}: {
  shipmentId: string;
  taskId: string;
  taskNumber: number;
  carrierIds: string;
  dispatchCenterIds: string;
  onClose: () => void;
}) {
  const query = useTasks({
    shipmentId,
    limit: "all",
    carrierIds,
    dispatchCenterIds: dispatchCenterIds || undefined,
  });
  const source = useAdminIntakeSourceTask(
    taskId,
    JSON.stringify([carrierIds, dispatchCenterIds])
  );
  const client = useQueryClient();
  const denied = [source.error, query.error].some(
    (error) =>
      axios.isAxiosError(error) &&
      [401, 403, 404].includes(error.response?.status ?? 0)
  );
  useEffect(() => {
    if (denied) {
      client.removeQueries({ queryKey: ["admin", "intake-source-task"] });
      client.removeQueries({ queryKey: ["tasks", { shipmentId }] });
    }
  }, [denied, client, shipmentId]);
  const ready =
    source.isSuccess &&
    !!query.data &&
    !source.isFetching &&
    !query.isFetching &&
    !source.isError &&
    !query.isError;
  const tasks =
    ready && source.data
      ? [
          {
            ...query.data?.data.find((task) => task.id === taskId),
            ...source.data,
            proof: source.data.proof ?? undefined,
          },
          ...(query.data?.data.filter((task) => task.id !== taskId) ?? []),
        ]
      : [];
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Source TSK-{taskNumber} and Shipment work</DialogTitle>
          <DialogDescription>
            Original Task, Trip and proof history. Delivery planning stays
            independent of center receipt.
          </DialogDescription>
        </DialogHeader>
        <Button
          variant="outline"
          disabled={source.isFetching || query.isFetching}
          onClick={() => void Promise.all([source.refetch(), query.refetch()])}
        >
          Refresh Task context
        </Button>
        {source.isError || query.isError ? (
          <p role="alert">
            {denied
              ? "Task access unavailable. Cached Task and proof context are hidden."
              : source.isError
                ? "Original Task context could not be loaded."
                : "Shipment work could not be loaded."}
          </p>
        ) : !ready ? (
          <p role="status">Loading Task context…</p>
        ) : (
          <>
            <ul className="space-y-2">
              {tasks.map((task) => (
                <li
                  key={task.id}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  TSK-{task.taskNumber} ·{" "}
                  {task.trip ? (
                    <>
                      Trip #{task.trip.tripNumber}
                      <ViewTripButton tripId={task.trip.id} size="sm" />
                    </>
                  ) : task.tripId ? (
                    <>
                      Trip {task.tripId}
                      <ViewTripButton tripId={task.tripId} size="sm" />
                    </>
                  ) : (
                    "No Trip assigned"
                  )}
                </li>
              ))}
            </ul>
            <TaskTimeline tasks={tasks} />
          </>
        )}
        <Button variant="outline" asChild>
          <Link to="/trips/tasks">Open existing Task planning</Link>
        </Button>
      </DialogContent>
    </Dialog>
  );
}
